/**
 * Game: a position plus the move history needed for repetition adjudication.
 *
 * This is the object higher layers (analysis, PGN, live play) should hold, since
 * the repetition law is history-dependent and a bare `Position` cannot express it.
 */

import { legalMoves } from "./movegen.js";
import { Position } from "./position.js";
import { parseFen, toFen, START_FEN } from "./fen.js";
import { repetitionKey, adjudicateCycle, chasedSquares, type CyclePly } from "./repetition.js";
import { inCheck } from "./attacks.js";
import { resolveStatus, GameResult } from "./status.js";
import { Color, NUM_SQUARES, type Move, type Piece } from "./types.js";

interface HistoryEntry {
  readonly move: Move;
  readonly captured: Piece | null;
  /** Repetition key of the position AFTER the move. */
  readonly key: string;
  /** Who made the move. */
  readonly mover: Color;
  /**
   * Whether the move left the opponent in check.
   *
   * Recorded at play() time because it cannot be recovered afterwards without
   * replaying, and the repetition law needs it for every ply of a cycle. This is
   * the fact whose absence made perpetual-check adjudication dead code.
   */
  readonly gaveCheck: boolean;
  /** Stable ids of the enemy pieces this move leaves under threat (chase rules applied). */
  readonly chasedIds: readonly number[];
}

export class Game {
  readonly position: Position;
  private readonly history: HistoryEntry[] = [];
  /** Repetition keys of every position reached, including the start. */
  private readonly keys: string[];
  /**
   * Stable piece identity: `pieceId[square]` is a number that follows the piece
   * as it moves, or 0 for an empty square.
   *
   * The chase law asks whether the same piece was chased on every move of a
   * cycle, and a chased piece is usually running, so squares cannot answer it.
   * Ids are assigned once at construction and never reused: a captured piece's
   * id simply stops appearing, which is what should happen.
   */
  private readonly pieceId: number[];

  constructor(pos: Position = parseFen(START_FEN)) {
    this.position = pos;
    this.keys = [repetitionKey(pos)];
    this.pieceId = new Array<number>(NUM_SQUARES).fill(0);
    let next = 1;
    for (let sq = 0; sq < NUM_SQUARES; sq += 1) {
      if (pos.pieceAt(sq)) this.pieceId[sq] = next++;
    }
  }

  static fromFen(fen: string): Game {
    return new Game(parseFen(fen));
  }

  fen(): string {
    return toFen(this.position);
  }

  legalMoves(): Move[] {
    return legalMoves(this.position);
  }

  /** Apply a move, recording history for repetition detection. */
  play(move: Move): void {
    const mover = this.position.sideToMove;
    const captured = this.position.makeMove(move);
    // The mover's id follows it; anything standing on the destination has been
    // captured, and its id is gone with it.
    this.pieceId[move.to] = this.pieceId[move.from]!;
    this.pieceId[move.from] = 0;
    // makeMove has flipped the side to move, so "is the side to move in check"
    // is exactly "did the mover deliver check".
    const gaveCheck = inCheck(this.position, this.position.sideToMove);
    const chasedIds: number[] = [];
    // Only threats made by the piece that just moved count -- see chasedSquares.
    for (const sq of chasedSquares(this.position, mover, move.to)) {
      const id = this.pieceId[sq]!;
      if (id > 0) chasedIds.push(id);
    }
    const key = repetitionKey(this.position);
    this.history.push({ move, captured, key, mover, gaveCheck, chasedIds });
    this.keys.push(key);
  }

  /** How many times the current position has occurred in this game (>=1). */
  currentRepetitionCount(): number {
    const key = this.keys[this.keys.length - 1]!;
    let n = 0;
    for (const k of this.keys) if (k === key) n += 1;
    return n;
  }

  /**
   * Result considering position-local terminal states (mate, stalemate-loss,
   * natural draw) ONLY. Repetition is deliberately not folded in here: an
   * analysis board wants to walk through a repeating line without the position
   * being declared over. Callers that own a real game ask repetitionOutcome()
   * as well -- see outcome().
   */
  status(): GameResult {
    return resolveStatus(this.position);
  }

  /**
   * The plies of the most recent cycle: the moves that led from the previous
   * occurrence of this exact position back to it. Null if it has not occurred
   * before.
   *
   * keys[0] is the start position and keys[i] is the position after
   * history[i-1], so a match at keys[prev] means the cycle is history[prev..end].
   */
  repetitionCycle(): CyclePly[] | null {
    const key = this.keys[this.keys.length - 1]!;
    let prev = -1;
    for (let i = this.keys.length - 2; i >= 0; i -= 1) {
      if (this.keys[i] === key) {
        prev = i;
        break;
      }
    }
    if (prev < 0) return null;
    return this.history
      .slice(prev)
      .map((h) => ({ mover: h.mover, gaveCheck: h.gaveCheck, chasedIds: h.chasedIds }));
  }

  /**
   * The repetition ruling for the current position, or null if the position has
   * not recurred often enough to be adjudicated.
   *
   * `threshold` is the number of occurrences that triggers a ruling; 3 matches
   * the point at which implementations conventionally stop the game, and is the
   * point at which this server used to declare a draw.
   *
   * Both halves of the law are applied: the sole perpetual checker loses, and
   * failing that the sole perpetual chaser loses. An idle repetition, a cycle
   * where both sides are equally at fault, and a cycle mixing a check with a
   * chase (一將一捉) are all draws.
   *
   * The chase half is implemented but not validated against the WXF casebook --
   * see CHASE_VALIDATED_AGAINST_CASEBOOK in repetition.ts before writing any
   * copy that claims otherwise.
   */
  repetitionOutcome(threshold = 3): GameResult | null {
    if (this.currentRepetitionCount() < threshold) return null;
    const cycle = this.repetitionCycle();
    if (!cycle || cycle.length === 0) return null;
    return adjudicateCycle(cycle);
  }

  /**
   * The full result for a real game: position-local terminal states first, then
   * the repetition ruling. This is what a server should call.
   */
  outcome(threshold = 3): GameResult {
    const local = resolveStatus(this.position);
    if (local !== GameResult.Ongoing) return local;
    return this.repetitionOutcome(threshold) ?? GameResult.Ongoing;
  }

  moveCount(): number {
    return this.history.length;
  }
}
